import { MessageCircle } from "lucide-react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { buildWhatsappUrl } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Floating WhatsApp click-to-chat button.
 *
 * Reuses the same site-configured number and wa.me link generation as the
 * existing Journal WhatsApp implementation. Rendered as a circular icon
 * button with a hover label, matching the existing floating-control style.
 */
export function FloatingWhatsAppButton() {
  const config = useSiteConfig();
  const waUrl = buildWhatsappUrl(config.whatsappNumber, config.whatsappDefaultMessage);

  const handleClick = () => {
    trackCtaClick({
      action: "whatsapp_chat",
      location: "floating-cta",
      label: "Chat on WhatsApp",
      href: waUrl,
    });
  };

  return (
    <div className="flex flex-col items-end gap-3 pointer-events-none pr-4 sm:pr-6 group">
      <Button
        asChild
        className={cn(
          "rounded-full h-14 w-14 shadow-xl border-2 transition-all duration-300 pointer-events-auto",
          "bg-accent border-accent text-accent-foreground hover:bg-accent/90 hover:scale-105 active:scale-95"
        )}
        aria-label="Chat on WhatsApp"
      >
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
        >
          <MessageCircle className="h-6 w-6" />
        </a>
      </Button>
      <span className="bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm px-3 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase text-muted-foreground shadow-sm border border-border pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        Chat on WhatsApp
      </span>
    </div>
  );
}
